import { create } from 'zustand';
import { jwtIsPlatform, jwtPermissions } from '@/lib/jwt';
import { session } from '@/api/session';
import { authApi, usersApi } from '@/api/resources';
import { forgetSessionState } from '@/app/queryClient';
import i18n from '@/i18n';
import type { LoginRequest, TokenResponse, UserResponse } from '@/types/api';
import { holdsPermission, type Permission } from '@/api/permissions';

type Phase = 'booting' | 'authenticated' | 'anonymous';

interface AuthState {
  phase: Phase;
  user: UserResponse | null;
  permissions: Set<string>;
  roleCode: string | null;
  /**
   * From the `plat` JWT claim, i.e. the role's `is_platform` flag. Gates
   * platform-only navigation for tenants, who may hold the same permission
   * codes (a tenant owner has `ORGANIZATION_READ` too) but are not staff.
   */
  isPlatform: boolean;
  /** Restores a session from the persisted refresh token on first paint. */
  bootstrap: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<UserResponse>;
  acceptTokens: (tokens: TokenResponse) => Promise<UserResponse>;
  logout: () => Promise<void>;
  setUser: (user: UserResponse) => void;
  can: (permission: Permission | Permission[]) => boolean;
  canAny: (permissions: Permission[]) => boolean;
}

/**
 * Restoring a session is single-flighted. React StrictMode invokes the mount
 * effect twice in development, and two tabs can restore at the same moment —
 * either way the same refresh token would be presented twice, which the backend
 * treats as a leak and answers by revoking every session for that user.
 */
let bootstrapping: Promise<void> | null = null;

function hydrate(tokens: TokenResponse) {
  session.set({
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    expiresIn: tokens.expiresIn,
  });
  const accessToken = tokens.accessToken ?? '';
  return { permissions: new Set(jwtPermissions(accessToken)), isPlatform: jwtIsPlatform(accessToken) };
}

/**
 * A signed-in user's `preferredLocale` wins over the stored default the
 * moment a session is established; the language switch still works after
 * that, and its choice is what the next visit starts from.
 */
function applyPreferredLocale(user: UserResponse) {
  const preferred = user.preferredLocale;
  if (preferred && !i18n.language.startsWith(preferred)) void i18n.changeLanguage(preferred);
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  phase: 'booting',
  user: null,
  permissions: new Set<string>(),
  roleCode: null,
  isPlatform: false,

  bootstrap: () => {
    bootstrapping ??= (async () => {
      const refreshToken = session.refreshToken;
      if (!refreshToken) {
        set({ phase: 'anonymous' });
        return;
      }
      try {
        const tokens = await authApi.refresh(refreshToken);
        const { permissions, isPlatform } = hydrate(tokens);
        const user = tokens.user ?? (await usersApi.me());
        set({ phase: 'authenticated', user, permissions, isPlatform, roleCode: user.roleCode ?? null });
        applyPreferredLocale(user);
      } catch {
        session.clear();
        forgetSessionState();
        set({ phase: 'anonymous', user: null, permissions: new Set(), isPlatform: false, roleCode: null });
      } finally {
        bootstrapping = null;
      }
    })();
    return bootstrapping;
  },

  login: async (credentials) => get().acceptTokens(await authApi.login(credentials)),

  acceptTokens: async (tokens) => {
    /*
     * First, before a single call is made as the new holder of the token.
     * Signing out already cleared the cache; this clears it again because the
     * gap between the two is not empty — a late answer to the previous
     * session's request, or a tab that was still refetching, would otherwise
     * be the first thing this session reads. It also covers the wizard's
     * token exchange, where the same person gains an organization and
     * everything read without one has to be asked again.
     */
    forgetSessionState();
    const { permissions, isPlatform } = hydrate(tokens);
    const user = tokens.user ?? (await usersApi.me());
    set({ phase: 'authenticated', user, permissions, isPlatform, roleCode: user.roleCode ?? null });
    applyPreferredLocale(user);
    return user;
  },

  logout: async () => {
    const refreshToken = session.refreshToken;
    try {
      if (refreshToken) await authApi.logout(refreshToken);
    } catch {
      /* signing out locally matters more than the round trip succeeding */
    }
    session.clear();
    forgetSessionState();
    set({ phase: 'anonymous', user: null, permissions: new Set(), isPlatform: false, roleCode: null });
  },

  setUser: (user) => set({ user, roleCode: user.roleCode ?? null }),

  can: (permission) => {
    const held = get().permissions;
    const needed = Array.isArray(permission) ? permission : [permission];
    return needed.every((p) => holdsPermission(held, p));
  },

  canAny: (permissions) => {
    const held = get().permissions;
    return permissions.some((p) => holdsPermission(held, p));
  },
}));
